import { useState } from "react";
import { useVoice } from "../hooks/useVoice";
import { useRefineWs } from "../hooks/useRefineWs";
import { useApplyVoice } from "../hooks/useApplyVoice";
import { FeedbackBox } from "../components/FeedbackBox";
import { VoiceDiffView } from "../components/VoiceDiffView";
import { Skeleton } from "../components/ui/Skeleton";

const phaseLabel: Record<string, string> = {
  idle: "Refinar com IA",
  fetching_voice: "Buscando voz...",
  calling_opus: "Chamando Opus...",
  validating: "Validando...",
  done: "Refinar com IA",
  error: "Refinar com IA",
};

export const SettingsVoice = () => {
  const [showFullPrompt, setShowFullPrompt] = useState(false);
  // Incrementado após apply success pra forçar remount do FeedbackBox e limpar a textarea.
  const [feedbackResetKey, setFeedbackResetKey] = useState(0);

  const { data, isLoading, error } = useVoice();
  const { refine, phase, result: candidate, error: refineError, reset } = useRefineWs();
  const applyMutation = useApplyVoice();

  const isRefining = phase !== "idle" && phase !== "done" && phase !== "error";

  const handleRefine = (feedback: string) => {
    refine(feedback);
  };

  const handleApply = () => {
    if (!candidate) return;
    applyMutation.mutate(candidate, {
      onSuccess: () => {
        reset();
        setFeedbackResetKey((k) => k + 1);
      },
    });
  };

  if (isLoading) {
    return (
      <div role="status" aria-label="Carregando" className="p-8 max-w-4xl mx-auto space-y-6">
        <div className="space-y-2">
          <Skeleton className="h-7 w-48" />
          <Skeleton className="h-4 w-64" />
        </div>
        <div className="space-y-3">
          <Skeleton className="h-4 w-full" />
          <Skeleton className="h-4 w-5/6" />
          <Skeleton className="h-4 w-4/6" />
        </div>
        <Skeleton className="h-32 w-full" />
      </div>
    );
  }
  if (error) {
    if ((error as Error).message?.includes("404")) {
      return (
        <div className="p-8 max-w-3xl mx-auto">
          <h1 className="text-2xl font-semibold mb-4">Voice Profile</h1>
          <p className="text-gray-600">
            Voice profile ainda não foi gerado. Rode{" "}
            <code>npm run bootstrap:voice</code> primeiro.
          </p>
        </div>
      );
    }
    return (
      <div className="p-8 text-red-600">Erro: {(error as Error).message}</div>
    );
  }
  if (!data) return null;

  return (
    <div className="p-8 max-w-4xl mx-auto space-y-6">
      <header>
        <h1 className="text-2xl font-semibold">Voice Profile</h1>
        <p className="text-sm text-gray-500">
          v{data.version} · atualizado{" "}
          {new Date(data.generatedAt).toLocaleString("pt-BR")}
        </p>
      </header>

      <section className="border rounded-lg p-4 bg-gray-50">
        <h2 className="text-lg font-medium mb-2">Resumo</h2>
        <p className="text-sm text-gray-700 whitespace-pre-wrap">
          {data.profile.summary}
        </p>
        <div className="mt-3 flex flex-wrap gap-2">
          {data.profile.themes.primary.map((t) => (
            <span
              key={t}
              className="text-xs bg-blue-100 text-blue-800 px-2 py-1 rounded"
            >
              {t}
            </span>
          ))}
        </div>
        <button
          onClick={() => setShowFullPrompt((v) => !v)}
          className="mt-3 text-sm text-blue-600 hover:underline"
        >
          {showFullPrompt ? "Ocultar" : "Ver"} system prompt completo
        </button>
        {showFullPrompt && (
          <pre className="mt-3 p-3 bg-white border rounded text-xs whitespace-pre-wrap font-mono">
            {data.systemPrompt}
          </pre>
        )}
      </section>

      <section>
        <h2 className="text-lg font-medium mb-3">Refinar</h2>
        <FeedbackBox
          key={feedbackResetKey}
          onSubmit={handleRefine}
          isLoading={isRefining}
          buttonLabel={phaseLabel[phase]}
        />
        {refineError && (
          <p className="mt-2 text-sm text-red-600">
            Erro: {refineError}
          </p>
        )}
      </section>

      {candidate && (
        <section className="border-t pt-6">
          <h2 className="text-lg font-medium mb-3">Preview</h2>
          <VoiceDiffView
            oldSystemPrompt={data.systemPrompt}
            newSystemPrompt={candidate.systemPrompt}
            oldProfile={data.profile}
            newProfile={candidate.profile}
            onApply={handleApply}
            onDiscard={() => reset()}
            isApplying={applyMutation.isPending}
          />
          {applyMutation.isError && (
            <p className="mt-2 text-sm text-red-600">
              Erro ao aplicar: {(applyMutation.error as Error).message}
            </p>
          )}
        </section>
      )}
    </div>
  );
};
